import { useEffect, useState } from 'react';
import { Bell, BellOff, CalendarDays, ClipboardList, FileText, Megaphone, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
  type NotificationsMeta,
} from '../api/notifications';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { notifLink } from '@/lib/notification-link';

function notifIcon(type: string) {
  if (type === 'ANNOUNCEMENT') return Megaphone;
  if (type.startsWith('ATTENDANCE')) return ClipboardList;
  if (type.startsWith('LEAVE')) return CalendarDays;
  if (type.startsWith('CLAIM')) return Receipt;
  if (type === 'PAYSLIP_PUBLISHED') return FileText;
  return Bell;
}

export default function Notifications() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [meta, setMeta] = useState<NotificationsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();

  async function load(p: number) {
    setLoading(true);
    const res = await getNotifications(p);
    if (res.data) {
      setItems((prev) => p === 1 ? res.data!.data : [...prev, ...res.data!.data]);
      setMeta(res.data.meta);
    }
    setLoading(false);
  }

  useEffect(() => { load(1); }, []);

  // Auto-refresh every 20 seconds while on this page
  useEffect(() => {
    const id = setInterval(() => {
      setPage(1);
      load(1);
    }, 20_000);
    return () => clearInterval(id);
  }, []);

  async function handleMarkRead(id: string) {
    await markNotificationRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setMeta((m) => m ? { ...m, unread_count: Math.max(0, m.unread_count - 1) } : m);
  }

  async function handleMarkAll() {
    setMarkingAll(true);
    await markAllNotificationsRead();
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setMeta((m) => m ? { ...m, unread_count: 0 } : m);
    setMarkingAll(false);
  }

  function handleLoadMore() {
    const next = page + 1;
    setPage(next);
    load(next);
  }

  function handleItemClick(item: NotificationItem) {
    if (!item.isRead) handleMarkRead(item.id);
    const link = notifLink(item.type, item.relatedResourceId);
    if (link) navigate(link);
  }

  const hasMore = meta ? meta.current_page < meta.last_page : false;
  const unread = meta?.unread_count ?? 0;

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-8">
      <PageHeader
        title="Notifications"
        subtitle={unread > 0 ? `${unread} unread` : 'All caught up'}
      />

      {unread > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleMarkAll} disabled={markingAll}>
            {markingAll ? 'Marking…' : 'Mark all as read'}
          </Button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Bell className="h-10 w-10 opacity-20" />
          <p className="text-sm">Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <BellOff className="h-10 w-10 opacity-20" />
          <p className="text-sm font-medium">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const Icon = notifIcon(item.type);
            const link = notifLink(item.type, item.relatedResourceId);
            const isAnnouncement = item.type === 'ANNOUNCEMENT';

            return (
              <Card
                key={item.id}
                className={cn(
                  'transition-colors',
                  link && 'cursor-pointer hover:bg-muted/50',
                  !item.isRead && 'border-primary/40 bg-primary/5'
                )}
                onClick={() => handleItemClick(item)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      item.isRead ? 'bg-muted' : 'bg-primary/10'
                    )}>
                      <Icon className={cn('h-4 w-4', item.isRead ? 'text-muted-foreground' : 'text-primary')} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn('text-sm leading-snug', !item.isRead ? 'font-semibold' : 'font-medium')}>
                          {item.title}
                        </p>
                        {!item.isRead && (
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                        )}
                      </div>

                      {isAnnouncement ? (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {item.message.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                        </p>
                      ) : (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.message}</p>
                      )}

                      <p className="mt-1 text-[10px] text-muted-foreground/60">{formatDate(item.createdAt)}</p>

                      {link && (
                        <p className="mt-1 text-xs text-primary">
                          {isAnnouncement ? 'View announcement →' : 'View details →'}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
